import React from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
// TODO: - localize apexchart
// import ar from "apexcharts/dist/locales/ar.json";
// import en from "apexcharts/dist/locales/en.json";

import { useCommonSelector } from '@wajeez-web/stores/common';
import moment from 'moment';

export type ChartTypesType =
  | 'line'
  | 'area'
  | 'bar'
  | 'histogram'
  | 'pie'
  | 'donut'
  | 'radialBar'
  | 'scatter'
  | 'bubble'
  | 'heatmap'
  | 'treemap'
  | 'boxPlot'
  | 'candlestick'
  | 'radar'
  | 'polarArea'
  | 'rangeBar';
interface ChartDisplayProps {
  chartType: ChartTypesType;
  seriesData: ApexOptions['series'];
  labels?: string[];
  formatter?(
    value: string,
    timestamp?: number | undefined,
    opts?: any
  ): string | string[];
  xAxistype?: 'category' | 'datetime' | 'numeric' | undefined;
  loading?: boolean;
  tooltip?: ApexOptions['tooltip'];
  xaxis?: ApexOptions['xaxis'];
  chart?: ApexOptions['chart'];
  plotOptions?: ApexOptions['plotOptions'];
  noDataText?: string;
}

const ChartDisplay: React.FC<ChartDisplayProps> = ({
  seriesData,
  chartType,
  labels = [],
  formatter,
  xAxistype,
  loading = false,
  tooltip,
  xaxis,
  chart,
  plotOptions,
  noDataText,
}) => {
  const { lang } = useCommonSelector((store) => store.common.settings);
  const getOptions = (): ApexOptions | undefined => {
    switch (chartType) {
      case 'line':
        return {
          dataLabels: {
            enabled: false,
          },
          stroke: {
            curve: 'smooth',
          },
          yaxis: {
            opposite: lang === 'ar' ? true : false,
            labels: {
              formatter: (value) => {
                return Number(value).toFixed(0);
              },
            },
          },
        };
      case 'area':
        return {
          stroke: {
            width: 4,
            curve: 'smooth',
          },
        };

      case 'pie':
        return {
          dataLabels: {
            enabled: true,
            formatter: function (val) {
              return Number(val).toFixed(2) + '%';
            },
          },
          labels: labels,
          responsive: [
            {
              breakpoint: 600,
              options: {
                chart: {
                  width: 350,
                },
                legend: {
                  position: 'bottom',
                },
              },
            },
          ],
        };
      default:
        return {
          yaxis: {
            opposite: lang === 'ar' ? true : false,
          },
        };
    }
  };
  return (
    <div className="h-full">
      {loading ? (
        <div className="loading h-[350px] flex justify-center items-center align-middle">
          <div className="spinner-border text-primary" role="status">
            <span className="">
              <ReturnTranslatedText text="general.Loading" />
            </span>
          </div>
        </div>
      ) : (
        <Chart
          loading={true}
          options={{
            tooltip: {
              ...tooltip,
            },
            plotOptions: {
              ...plotOptions,
            },
            legend: {
              show: true,
              showForSingleSeries: false,
              showForNullSeries: true,
              showForZeroSeries: true,
              position: 'bottom',
              horizontalAlign: 'center',

              itemMargin: {
                horizontal: 10,
                vertical: 5,
              },
              markers: {
                width: 12,
                height: 12,
                strokeWidth: 0,
                strokeColor: '#fff',
                fillColors: undefined,
                radius: 12,
                customHTML: undefined,
                onClick: undefined,
                offsetX: lang === 'ar' ? 3 : 0,
                offsetY: lang === 'ar' ? -1 : 0,
              },
            },
            xaxis: {
              type: xAxistype,
              offsetX: lang === 'ar' ? 10 : 0,
              offsetY: 0,
              labels: {
                rotate: lang === 'ar' ? 45 : -45,
                rotateAlways: false,
                formatter,
              },
              ...xaxis,
            },
            yaxis: {
              opposite: lang === 'ar' ? true : false,
              labels: {
                align: 'center',
                offsetX: lang === 'ar' ? -10 : 0,
                formatter: (value) => {
                  return Number(value).toFixed(0);
                },
              },
            },
            chart: {
              // locales: [ar, en],
              // defaultLocale: lang === "ar" ? "ar" : "en",

              animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 100,
                animateGradually: {
                  enabled: true,
                  delay: 10,
                },
                dynamicAnimation: {
                  enabled: true,
                  speed: 350,
                },
              },
              toolbar: {
                autoSelected: 'pan',
                show: false,
              },
              zoom: {
                enabled: false,
              },
              ...chart,
            },
            noData: {
              text: noDataText || 'No Data',
              align: 'center',
              verticalAlign: 'middle',
              offsetX: 0,
              offsetY: -20,
              style: {
                color: undefined,
                fontSize: '17px',
                fontFamily: 'Tajawal',
              },
            },
            dataLabels: {
              enabled: false,
            },
            colors: ['#1f7983'],
            ...getOptions(),
          }}
          series={seriesData}
          type={chartType}
          height={350}
        />
      )}
    </div>
  );
};

export default ChartDisplay;
